/**
 * Entry Detail Page
 *
 * Shows entry details with editing capabilities.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEntry, useEntryMutations } from '@/hooks/use-entries';
import { EntryTypeSelector } from '@/components/entries';
import {
  ENTRY_TYPE_ICONS,
  ENTRY_TYPE_LABELS,
  SOURCE_SYSTEM_LABELS,
  formatDuration,
} from '@/types/entry';
import type { EntryType } from '@/types/entry';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '--';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EntryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useEntry(id ?? null, true);
  const { updateEntry } = useEntryMutations();

  // Local state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSynopsis, setEditSynopsis] = useState('');
  const [editType, setEditType] = useState<EntryType>('conversation');
  const [editTags, setEditTags] = useState('');

  // Initialize edit values when data loads
  useEffect(() => {
    if (data?.entry) {
      setEditTitle(data.entry.userOverrides?.title || '');
      setEditSynopsis(data.entry.userOverrides?.synopsis || '');
      setEditType((data.entry.userOverrides?.entryType || data.entry.entryType) as EntryType);
      setEditTags((data.entry.userOverrides?.tags || []).join(', '));
    }
  }, [data]);

  const handleSave = async () => {
    if (!id) return;

    try {
      await updateEntry.mutateAsync({
        entryId: id,
        input: {
          title: editTitle || undefined,
          synopsis: editSynopsis || undefined,
          entryType: editType,
          tags: editTags ? editTags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        },
      });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update entry:', err);
    }
  };

  const handleCancel = () => {
    if (data?.entry) {
      setEditTitle(data.entry.userOverrides?.title || '');
      setEditSynopsis(data.entry.userOverrides?.synopsis || '');
      setEditType((data.entry.userOverrides?.entryType || data.entry.entryType) as EntryType);
      setEditTags((data.entry.userOverrides?.tags || []).join(', '));
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-100 rounded w-full mb-2" />
          <div className="h-4 bg-gray-100 rounded w-3/4 mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data?.entry) {
    return (
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="text-4xl mb-4">😕</div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            Entry not found
          </h2>
          <p className="text-gray-600 mb-4">
            {error?.message || 'The entry you are looking for does not exist.'}
          </p>
          <button
            onClick={() => navigate('/entries')}
            className="text-indigo-600 hover:text-indigo-700 font-medium"
          >
            ← Back to entries
          </button>
        </div>
      </div>
    );
  }

  const entry = data.entry;
  const associations = data.associations;
  const hasOverrides = entry.overrideMetadata?.hasUserOverrides;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Back link */}
      <Link
        to="/entries"
        className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1 mb-4"
      >
        ← Back to entries
      </Link>

      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        {/* Edit toggle */}
        <div className="flex justify-end mb-4">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              ✏️ Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateEntry.isPending}
                className="text-sm px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {updateEntry.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="flex items-start gap-3 mb-4">
          <span className="text-3xl">
            {ENTRY_TYPE_ICONS[entry.effectiveEntryType as keyof typeof ENTRY_TYPE_ICONS] || '📄'}
          </span>
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder={entry.title}
              className="flex-1 text-2xl font-bold border-b border-gray-300 focus:border-indigo-500 outline-none py-1"
            />
          ) : (
            <h1 className="text-2xl font-bold text-gray-900">
              {entry.effectiveTitle}
              {hasOverrides && (
                <span className="ml-2 text-sm font-normal text-indigo-500">
                  (edited)
                </span>
              )}
            </h1>
          )}
        </div>

        {/* Entry type */}
        {isEditing ? (
          <div className="mb-4">
            <EntryTypeSelector
              value={editType}
              onChange={setEditType}
            />
          </div>
        ) : (
          <div className="text-sm text-gray-600 mb-4">
            Type: {ENTRY_TYPE_LABELS[entry.effectiveEntryType as keyof typeof ENTRY_TYPE_LABELS] || entry.effectiveEntryType}
          </div>
        )}

        {/* Synopsis */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Synopsis
          </label>
          {isEditing ? (
            <textarea
              value={editSynopsis}
              onChange={(e) => setEditSynopsis(e.target.value)}
              placeholder={entry.synopsis || 'Add a synopsis...'}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          ) : (
            <p className="text-gray-700">
              {entry.effectiveSynopsis || <span className="text-gray-400 italic">No synopsis</span>}
            </p>
          )}
        </div>

        {/* Tags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              placeholder="Enter tags separated by commas"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {entry.effectiveTags.length > 0 ? (
                entry.effectiveTags.map((tag) => (
                  <span
                    key={tag}
                    className="text-sm px-2 py-1 rounded-full bg-indigo-50 text-indigo-700"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-gray-400 italic text-sm">No tags</span>
              )}
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="border-t border-gray-200 pt-4 mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Recorded</span>
            <div className="font-medium">{formatDate(entry.recordedAt)}</div>
          </div>
          <div>
            <span className="text-gray-500">Duration</span>
            <div className="font-medium">{formatDuration(entry.durationSeconds)}</div>
          </div>
          <div>
            <span className="text-gray-500">Source</span>
            <div className="font-medium capitalize">
              {SOURCE_SYSTEM_LABELS[entry.sourceSystem as keyof typeof SOURCE_SYSTEM_LABELS] || entry.sourceSystem}
            </div>
          </div>
          <div>
            <span className="text-gray-500">Status</span>
            <div className={`font-medium capitalize ${
              entry.processingStatus === 'complete' ? 'text-green-600' :
              entry.processingStatus === 'failed' ? 'text-red-600' :
              'text-yellow-600'
            }`}>
              {entry.processingStatus}
            </div>
          </div>
        </div>
      </div>

      {/* Associated Tasks */}
      {associations && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Associated Tasks ({associations.taskCount})
          </h2>
          {associations.tasks.length > 0 ? (
            <div className="space-y-3">
              {associations.tasks.map((task) => (
                <Link
                  key={task.id}
                  to={`/tasks?task=${task.id}`}
                  className="block p-3 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{task.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {task.status?.replace('_', ' ') || 'pending'}
                    </span>
                  </div>
                  {task.verificationStatus && task.verificationStatus !== 'verified' && (
                    <span className="text-xs text-yellow-600 mt-1 inline-block">
                      ⚠️ {task.verificationStatus}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No tasks have been created from this entry.
            </p>
          )}
        </div>
      )}

      {/* Source Details */}
      {entry.sourceMetadata && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Source Details
          </h2>
          <div className="space-y-2 text-sm">
            {entry.sourceMetadata.language && (
              <div className="flex justify-between">
                <span className="text-gray-500">Language</span>
                <span className="font-medium">{entry.sourceMetadata.language}</span>
              </div>
            )}
            {entry.sourceMetadata.wordCount && (
              <div className="flex justify-between">
                <span className="text-gray-500">Word Count</span>
                <span className="font-medium">{entry.sourceMetadata.wordCount.toLocaleString()}</span>
              </div>
            )}
            {entry.sourceMetadata.speakerCount && (
              <div className="flex justify-between">
                <span className="text-gray-500">Speakers</span>
                <span className="font-medium">{entry.sourceMetadata.speakerCount}</span>
              </div>
            )}
            {entry.sourceMetadata.plaudTemplateName && (
              <div className="flex justify-between">
                <span className="text-gray-500">Template</span>
                <span className="font-medium">{entry.sourceMetadata.plaudTemplateName}</span>
              </div>
            )}
            {entry.sourceMetadata.otterFolder && (
              <div className="flex justify-between">
                <span className="text-gray-500">Folder</span>
                <span className="font-medium">{entry.sourceMetadata.otterFolder}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
